import { Grid, IconButton } from '@material-ui/core'
import { makeStyles } from '@material-ui/styles'
import Actions from 'actions'
import Filter from 'components/common/icons/Filter'
import GrinMenu from 'components/common/menu/GrinMenu'
import FilterTagsList from 'components/common/tags/FilterTagsList'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import { DEFAULT_TAGS_FILTER } from 'consts/filterConsts'
import useGaFlags from 'hooks/useGaFlags'
import useWindowSize from 'hooks/useWindowSize'
import { isEmpty, isEqual } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { isMobile } from 'react-device-detect'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { trackEvent } from 'utils/analyticsUtils'
import { TAGS_LIST_COMPRESSION_THRESHOLD_DENSE } from 'utils/tagUtils'
import FilterMenu from './FilterMenu'
import FilterMenuMobile from './FilterMenu.mobile'
import usePatientsFilterData from './hooks/usePatientsFilterData'
import NewSavedViewModal from './NewSavedViewModal'
import PatientsSearch from './PatientsSearch'
import PatientsSort from './PatientsSort'
import PatientsTagsFilter from './PatientsTagsFilter'
import SavedViews from './SavedViews/SavedViews'
import useRolePermissions from 'hooks/useRolePermissions'
import { isTieredHIPlanForPlan } from 'hooks/useIsTieredHIPlan'
import usePlanLimits from 'hooks/usePlanLimits'
import useFeatureFlags from 'hooks/useFeatureFlags'
import useHiPracticeEligibility from 'hooks/useHiPracticeEligibility'
import { buildHiTrackedOnlyFilter, buildHiTrackedOnlyStatusFilter } from 'utils/searchUtils'

const useStyles = ({ width }) =>
  makeStyles(theme => ({
    container: {
      boxShadow: isMobile && '0px 2px 8px rgba(0, 0, 0, 0.15)',
      minHeight: !isMobile && 75,
      paddingBottom: isMobile && 15,
      paddingTop: isMobile && 15,
      paddingLeft: !isMobile ? 20 : 15,
      paddingRight: !isMobile ? 0 : 15,
      backgroundColor: '#FAFAFA',
      borderBottom: '1px solid var(--border-color-3)',
      borderTop: '1px solid var(--border-color-3)'
    },
    filterButton: {
      border: '1px solid #C4C4C4',
      width: 40,
      height: 40,
      padding: 0
    },
    filterContainer: {
      maxWidth: 165,
      userSelect: 'none'
    },
    filter: {
      boxShadow: !isMobile ? '0px 4px 30px rgba(0, 0, 0, 0.15)' : 'none',
      padding: isMobile && 30
    },
    tagList: {
      width: ({ width }) => width && width - 900
    },
    mobileFilterTagList: {
      margin: '10px 10px 0px 10px'
    },
    clearAll: {
      cursor: 'pointer',
      color: 'var(--text-color-3)',
      textDecoration: 'underline'
    }
  }))({ width })

const HI_TRACKED_ONLY_TAG = { type: 'hiTrackedOnly', value: 'hiTrackedOnly', tagClass: 'system' }

const PatientsFilter = ({
  onPatientsFilterChange = () => {},
  onFreeTextFilterChange = () => {},
  isFilterActivated,
  patientsFilter,
  freeTextFilter,
  isFilterOpen,
  setIsFilterOpen,
  hidePrograms,
  hiTrackedOnlyFilter,
  setHiTrackedOnlyFilter
}) => {
  const { width } = useWindowSize()
  const classes = useStyles({ width })
  const { t } = useTranslation()
  const dispatch = useDispatch()

  const {
    assigneTags,
    programTags,
    treatmentTags,
    statusTags,
    customTags,
    scanTags,
    filterableSystemTags,
    appliedFilterTags,
    mapSelectedFiltersToSearchInput,
    applyFilters,
    queryParams
  } = usePatientsFilterData()
  const gaFlags = useGaFlags()
  const { isHiUser } = useRolePermissions()
  const { grinPlanKey, grinPlanGroup, withScanTracking } = usePlanLimits()
  const isTieredHIPlan = useMemo(
    () => isTieredHIPlanForPlan({ grinPlanKey, grinPlanGroup, withScanTracking }),
    [grinPlanGroup, grinPlanKey, withScanTracking]
  )
  const featureFlags = useFeatureFlags()
  const { ffOnDoctorIds, ffOffDoctorIds } = useHiPracticeEligibility()

  const [selectedFilters, setSelectedFilters] = useState(
    isEmpty(patientsFilter) ? { ...DEFAULT_TAGS_FILTER } : patientsFilter
  )

  const selectedDoctorFilter = useSelector(state => state.chatReducer.filter.doctorFilter)
  const isAllPracticesMode = !selectedDoctorFilter
  const showHiTrackedOnly = isHiUser
  const hiTrackedOnlyStatusValues = useMemo(() => {
    if (grinPlanKey === 'grin-hi-limited') {
      return ['active-treatment']
    }

    if (isTieredHIPlan && featureFlags.hiTieredObsRetentionCopilot) {
      return ['active-treatment', 'observation', 'retention']
    }

    return ['active-treatment']
  }, [featureFlags.hiTieredObsRetentionCopilot, grinPlanKey, isTieredHIPlan])

  const [isHiTrackedOnlyOn, setIsHiTrackedOnlyOn] = useState(
    !!hiTrackedOnlyFilter || queryParams?.hiTrackedOnly === 'true'
  )
  const hiTrackedOnlyChecked = showHiTrackedOnly && isHiTrackedOnlyOn
  // Disable the toggle in all-practices mode until eligibility data has loaded,
  // so the user can't activate a filter that would silently produce no results.
  const hiTrackedOnlyDisabled = isAllPracticesMode && !ffOnDoctorIds.length && !ffOffDoctorIds.length

  // Defers filter application to Apply click.
  // Values: undefined = no pending change, null = pending clear, object = pending filter.
  const pendingCompoundFilterRef = useRef(undefined)

  const customTagsLoadingState = useSelector(state => state.practiceReducer.aggregatedCustomTags.status)

  const [filteredCustomTags, setFilteredCustomTags] = useState([...filterableSystemTags, ...customTags])

  const parsedTags = useMemo(() => {
    let parsedTags = []
    Object.keys(selectedFilters).forEach(filter => (parsedTags = [...parsedTags, ...selectedFilters[filter]]))

    if (isHiTrackedOnlyOn) {
      // Always hide individual HI status tags when the toggle is ON — the unified
      // "HI Tracked Only" chip replaces them. This also guards against stale status
      // tags that can briefly appear during practice-switch transitions.
      parsedTags = parsedTags.filter(tag => !(tag.type === 'status' && hiTrackedOnlyStatusValues.includes(tag.value)))
      parsedTags = [...parsedTags, HI_TRACKED_ONLY_TAG]
    }

    return parsedTags
  }, [selectedFilters, isHiTrackedOnlyOn, hiTrackedOnlyStatusValues])
  const selectedFiltersToSearchInput = useMemo(
    () => mapSelectedFiltersToSearchInput({ selectedFilters }),
    [mapSelectedFiltersToSearchInput, selectedFilters]
  )

  const handleOpenFiltersMenu = useCallback(() => {
    setIsFilterOpen(true)
    setFilteredCustomTags([...filterableSystemTags, ...customTags])
  }, [setIsFilterOpen, filterableSystemTags, customTags])

  const handleFilterApply = useCallback(() => {
    trackEvent('Patients filter - tags filter applied', {
      tagFilters: parsedTags
    })

    if (pendingCompoundFilterRef.current !== undefined) {
      setHiTrackedOnlyFilter?.(pendingCompoundFilterRef.current)
      pendingCompoundFilterRef.current = undefined
    }

    const filtersToApply = mapSelectedFiltersToSearchInput({ selectedFilters })
    applyFilters(filtersToApply, { hiTrackedOnly: isHiTrackedOnlyOn ? 'true' : undefined })
    setIsFilterOpen(false)
  }, [
    setIsFilterOpen,
    mapSelectedFiltersToSearchInput,
    selectedFilters,
    applyFilters,
    parsedTags,
    setHiTrackedOnlyFilter,
    isHiTrackedOnlyOn
  ])

  const isTagSelected = useCallback(
    tag => !!selectedFilters[tag.type]?.find(currTag => currTag.value === tag.value),
    [selectedFilters]
  )

  const handleClearTag = useCallback(
    tag => {
      trackEvent('Patients filter - clear tag', {
        value: tag.value
      })

      if (tag.type === 'hiTrackedOnly') {
        setIsHiTrackedOnlyOn(false)
        setHiTrackedOnlyFilter?.(null)
        pendingCompoundFilterRef.current = undefined
        applyFilters(mapSelectedFiltersToSearchInput({ selectedFilters }), { hiTrackedOnly: undefined })
        return
      }

      const updatedFilteres = {
        ...selectedFilters,
        [tag.type]: selectedFilters[tag.type].filter(currTag => currTag.value !== tag.value)
      }

      setSelectedFilters(updatedFilteres)
      applyFilters(mapSelectedFiltersToSearchInput({ selectedFilters: updatedFilteres }))
    },
    [setSelectedFilters, selectedFilters, mapSelectedFiltersToSearchInput, applyFilters, setHiTrackedOnlyFilter]
  )

  const handleFilterChange = useCallback(
    selected => {
      isTagSelected(selected)
        ? setSelectedFilters(currSelected => {
            return {
              ...currSelected,
              [selected.type]: currSelected[selected.type].filter(tag => tag.value !== selected.value)
            }
          })
        : setSelectedFilters(currSelected => {
            return {
              ...currSelected,
              [selected.type]: [...currSelected[selected.type], selected]
            }
          })
    },
    [isTagSelected]
  )

  const handleHiTrackedOnlyToggled = useCallback(() => {
    trackEvent('Patients filter - HI tracked only toggled', {
      enabled: !hiTrackedOnlyChecked,
      mode: isAllPracticesMode ? 'all-practices' : 'single-practice',
      ffOnCount: ffOnDoctorIds.length,
      ffOffCount: ffOffDoctorIds.length
    })

    if (hiTrackedOnlyChecked) {
      setIsHiTrackedOnlyOn(false)
      pendingCompoundFilterRef.current = null
      return
    }

    setIsHiTrackedOnlyOn(true)
    if (isAllPracticesMode) {
      pendingCompoundFilterRef.current = buildHiTrackedOnlyFilter(ffOnDoctorIds, ffOffDoctorIds)
    } else {
      pendingCompoundFilterRef.current = buildHiTrackedOnlyStatusFilter(hiTrackedOnlyStatusValues)
    }
  }, [ffOffDoctorIds, ffOnDoctorIds, hiTrackedOnlyChecked, hiTrackedOnlyStatusValues, isAllPracticesMode])

  const handleClearSelectedFilters = useCallback(() => {
    setSelectedFilters({ ...DEFAULT_TAGS_FILTER })
    setIsHiTrackedOnlyOn(false)
    pendingCompoundFilterRef.current = null
  }, [])

  const handleClearAll = useCallback(() => {
    applyFilters({}, { hiTrackedOnly: undefined })
    setIsHiTrackedOnlyOn(false)
    setHiTrackedOnlyFilter?.(null)
    pendingCompoundFilterRef.current = undefined
  }, [applyFilters, setHiTrackedOnlyFilter])

  const handleCustomTagsFilter = useCallback(
    filter => {
      const mergedTags = [...filterableSystemTags, ...customTags]
      const filteredTags = mergedTags.filter(
        tag => tag.label.toLowerCase().includes(filter.toLowerCase()) || isTagSelected(tag)
      )

      setFilteredCustomTags(filteredTags)
    },
    [filterableSystemTags, customTags, isTagSelected]
  )

  const handleChangeNameFilter = useCallback(
    value => {
      onFreeTextFilterChange(value)
      if (value !== freeTextFilter) {
        trackEvent('Patients filter - Patients search applied', {
          value
        })
      }
    },
    [onFreeTextFilterChange, freeTextFilter]
  )

  const handleSavedViewSelected = useCallback(
    view => {
      trackEvent('Saved views - View selected', {
        view: view.name
      })
      applyFilters(view.filters, { hiTrackedOnly: undefined })
      setIsHiTrackedOnlyOn(false)
      setHiTrackedOnlyFilter?.(null)
      pendingCompoundFilterRef.current = undefined
    },
    [applyFilters, setHiTrackedOnlyFilter]
  )

  useEffect(() => {
    if (!isFilterOpen) {
      setSelectedFilters({ ...DEFAULT_TAGS_FILTER, ...appliedFilterTags })
    }
  }, [isFilterOpen, appliedFilterTags])

  useEffect(() => {
    setFilteredCustomTags([...filterableSystemTags, ...customTags])
  }, [filterableSystemTags, customTags])

  useEffect(() => {
    if (isFilterOpen && !customTagsLoadingState) {
      dispatch(Actions.fetchAggregatedPracticeCustomTags())
    }
  }, [isFilterOpen, customTagsLoadingState, dispatch])

  // --- HI filter effects (two cooperating effects) ---
  //
  // Both effects use functional state updates with deep-equality checks to prevent
  // unnecessary re-fetches. buildHiTrackedOnlyFilter/StatusFilter always return new
  // objects, but the content may be identical — without this guard, every call would
  // trigger a state change → usePatientsFilter fetch effect → redundant API call.
  //
  // 1. Practice-switch: rebuilds hiTrackedOnlyFilter for the new mode when the user
  //    switches between all-practices and single-practice while the toggle is ON.
  //    May use stale ffOnDoctorIds/ffOffDoctorIds if data hasn't arrived yet —
  //    effect #2 below covers that case when the data updates.
  useEffect(() => {
    if (!isHiTrackedOnlyOn) return
    const next = isAllPracticesMode
      ? buildHiTrackedOnlyFilter(ffOnDoctorIds, ffOffDoctorIds)
      : buildHiTrackedOnlyStatusFilter(hiTrackedOnlyStatusValues)
    setHiTrackedOnlyFilter?.(prev => (isEqual(prev, next) ? prev : next))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDoctorFilter])

  // 2. Eligibility-recompute: updates an already-applied filter when eligibility data
  //    changes (e.g., late-arriving assignedDoctors or plan changes). Also handles
  //    URL-restored state where the toggle is ON but the filter hasn't been built yet.
  useEffect(() => {
    if (!hiTrackedOnlyFilter && !isHiTrackedOnlyOn) return
    const next = isAllPracticesMode
      ? buildHiTrackedOnlyFilter(ffOnDoctorIds, ffOffDoctorIds)
      : buildHiTrackedOnlyStatusFilter(hiTrackedOnlyStatusValues)
    setHiTrackedOnlyFilter?.(prev => (isEqual(prev, next) ? prev : next))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ffOnDoctorIds, ffOffDoctorIds, hiTrackedOnlyStatusValues])

  return (
    <>
      <Grid container alignItems="center" justifyContent="space-between" className={classes.container}>
        <Grid item xs={12}>
          <Grid container alignItems="center" wrap="nowrap">
            <Grid item xs={isMobile && 10} className={classes.searchContainer}>
              <PatientsSearch onNameFilter={handleChangeNameFilter} />
            </Grid>
            <Grid>
              <PatientsSort />
            </Grid>
            <Grid item className={classes.filterContainer}>
              {isMobile ? (
                <>
                  <IconButton className={classes.filterButton} aria-label={'filter'} onClick={handleOpenFiltersMenu}>
                    <Filter />
                  </IconButton>
                  <FilterMenuMobile
                    open={!!isFilterOpen}
                    onClose={handleFilterApply}
                    onOpen={handleOpenFiltersMenu}
                    onFilterSelected={handleFilterChange}
                    onClearAll={handleClearSelectedFilters}
                    onApply={handleFilterApply}
                    onTagsFilterChanged={handleCustomTagsFilter}
                    selectedFilters={selectedFilters}
                    customTags={filteredCustomTags}
                    treatments={treatmentTags}
                    programs={programTags}
                    statuses={statusTags}
                    assigneTags={assigneTags}
                    scanTags={scanTags}
                    customTagsLoadingState={customTagsLoadingState}
                    showHiTrackedOnly={showHiTrackedOnly}
                    hiTrackedOnlyDisabled={hiTrackedOnlyDisabled}
                    hiTrackedOnlyChecked={hiTrackedOnlyChecked}
                    onHiTrackedOnlyToggled={handleHiTrackedOnlyToggled}
                  />
                </>
              ) : (
                <GrinMenu
                  id="patients-filter-button"
                  disableMenuFocus={true}
                  className={classes.filter}
                  onOpen={handleOpenFiltersMenu}
                  onClose={handleFilterApply}
                  open={!!isFilterOpen}
                  transformOrigin={{
                    vertical: window.screen.width < 1100 ? 'center' : -61,
                    horizontal: 'center'
                  }}
                  triggerComponent={
                    <PatientsTagsFilter isFilterOpen={isFilterOpen} selectedFilters={selectedFilters} />
                  }
                >
                  <FilterMenu
                    onFilterSelected={handleFilterChange}
                    onClearAll={handleClearSelectedFilters}
                    onApply={handleFilterApply}
                    onTagsFilterChanged={handleCustomTagsFilter}
                    selectedFilters={selectedFilters}
                    customTags={filteredCustomTags}
                    treatments={treatmentTags}
                    programs={programTags}
                    statuses={statusTags}
                    scanTags={scanTags}
                    hidePrograms={hidePrograms}
                    assigneTags={assigneTags}
                    customTagsLoadingState={customTagsLoadingState}
                    showHiTrackedOnly={showHiTrackedOnly}
                    hiTrackedOnlyDisabled={hiTrackedOnlyDisabled}
                    hiTrackedOnlyChecked={hiTrackedOnlyChecked}
                    onHiTrackedOnlyToggled={handleHiTrackedOnlyToggled}
                  />
                </GrinMenu>
              )}
            </Grid>
            <Grid item style={{ width: '80%' }}>
              <Grid container alignItems="center" justifyContent="space-between">
                <Grid item className={classes.tagList}>
                  {!isMobile && isFilterActivated && !isFilterOpen && (
                    <FilterTagsList
                      showIcon={false}
                      tags={parsedTags}
                      canDeleteCustom={true}
                      canDeleteSystem={true}
                      observeResize={false}
                      onDeleteTag={handleClearTag}
                      compressionThreshold={TAGS_LIST_COMPRESSION_THRESHOLD_DENSE}
                    />
                  )}
                </Grid>
                {!isMobile && (
                  <Grid item>
                    <Grid container alignItems="center" justifyContent="flex-end" spacing={1}>
                      {isFilterActivated && (
                        <Grid item>
                          <DazzedParagraph14
                            id="patients-filter-clear-all"
                            strong
                            className={classes.clearAll}
                            onClick={handleClearAll}
                          >
                            {t('pages.patients.patientsList.patientsTagFilter.clearLabel')}
                          </DazzedParagraph14>
                        </Grid>
                      )}
                      {gaFlags?.savedViews && (
                        <Grid item>
                          <SavedViews
                            isFilterActivated={isFilterActivated}
                            selectedFilters={selectedFiltersToSearchInput}
                            onSavedViewSelected={handleSavedViewSelected}
                          />
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
        {isMobile && isFilterActivated && !isFilterOpen && (
          <Grid item xs={12}>
            <FilterTagsList
              className={classes.mobileFilterTagList}
              showIcon={false}
              tags={parsedTags}
              canDeleteCustom={true}
              canDeleteSystem={true}
              observeResize={false}
              onDeleteTag={handleClearTag}
              compressionThreshold={TAGS_LIST_COMPRESSION_THRESHOLD_DENSE}
            />
          </Grid>
        )}
      </Grid>
      <NewSavedViewModal currentSelectedTags={selectedFilters} />
    </>
  )
}

export default PatientsFilter
